import { useCallback } from "react";
import { activeOfficeStore } from "@Front/Stores/ActiveOfficeStore";
import { useRolePermissionContext } from "@Front/Stores/RolePermissionsStore";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { isGuestFoldersAffiliation } from "@Front/Utils/preferredRealOfficeUidHelpers";
import { clearNotaryInvitedFoldersNavActive } from "@Front/Utils/notaryInvitedFoldersNavPersistence";
import {
	buildOfficeMenuRedirectTo,
	resolveGuestExitTargetOfficeUid,
} from "./exitGuestFoldersBeforeOfficeMenuHelpers";

/**
 * Avant d’ouvrir une page « Espace office », repasse aux dossiers de l’office (JWT + store)
 * si l’utilisateur est encore en vue « dossiers invités ».
 *
 * @returns `true` si `setActiveOffice` a été exécuté (navigation pleine page : le menu ne doit pas `router.push`).
 */
export function useExitGuestFoldersBeforeOfficeMenuNavigation(): (nextHref: string) => Promise<boolean> {
	const permissionContext = useRolePermissionContext();
	const userUid = permissionContext?.userId ?? null;
	const primaryOfficeUid = permissionContext?.primaryOfficeUid ?? null;

	return useCallback(async (nextHref: string): Promise<boolean> => {
		if (!userUid) {
			return false;
		}
		const active = activeOfficeStore.activeOffice;
		if (!active || !isGuestFoldersAffiliation(active)) {
			return false;
		}

		clearNotaryInvitedFoldersNavActive();

		const targetUid = resolveGuestExitTargetOfficeUid({
			lastRealOfficeUid: activeOfficeStore.peekLastRealOfficeUidBeforeGuest(),
			offices: activeOfficeStore.availableOffices,
			primaryOfficeUid,
		});

		if (!targetUid) {
			LoggerService.warn("⚠️ [officeMenu] Impossible de quitter les dossiers invités : aucun bureau métier résolu", {
				userUid: userUid.substring(0, 8) + "...",
			});
			return false;
		}

		await activeOfficeStore.setActiveOffice(userUid, targetUid, {
			redirectTo: buildOfficeMenuRedirectTo(nextHref),
		});
		return true;
	}, [userUid, primaryOfficeUid]);
}
